import React, { memo, useMemo } from 'react';
import { Shape } from '../../types/Shape';
import HTMLShapeBase from './HTMLShapeBase';
import { getYoutubeEmbedUrl } from '../../utils/youtubeUtils';

interface HTMLYoutubeProps {
  shape: Shape;
  isSelected: boolean;
  showResizeHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean;
  isEditing?: boolean;
  currentTool?: string;
  isAnyDragging?: boolean; // 다른 객체가 드래그 중인지
  isDragging?: boolean; // 자기 자신이 드래그 중인지
  onDragPrepare?: (shapeId: string) => void;
  onDragStart?: (shapeId: string) => void;
  onDragMove?: (shapeId: string, newX: number, newY: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onResize?: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeEnd?: () => void;
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onDoubleClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onMouseEnter?: (shapeId: string) => void;
  onMouseLeave?: () => void;
  onStageClick?: (canvasX: number, canvasY: number, e: React.MouseEvent) => void;
}

const HTMLYoutube: React.FC<HTMLYoutubeProps> = (props) => {
  const { shape, stageScale, isAnyDragging, isDragging } = props;
  
  // 자기 자신이 드래그 중이거나 다른 객체가 드래그 중일 때 iframe 비활성화
  const shouldDisableIframe = isDragging || isAnyDragging;

  if (!shape.youtubeVideoId) {
    return null;
  }

  // 메모이제이션으로 embedUrl 계산 최적화
  const embedUrl = useMemo(() => getYoutubeEmbedUrl(shape.youtubeVideoId!), [shape.youtubeVideoId]);
  const headerHeight = useMemo(() => 20 * stageScale, [stageScale]); // 10픽셀 헤더 (스케일 적용)

  return (
    <HTMLShapeBase {...props}>
      <div
        style={{
          width: '100%',
          height: '100%',
          minWidth: '100px', // 최소 크기 보장
          minHeight: '100px',
          // border: `${(shape.strokeWidth || 2) * stageScale}px solid ${shape.stroke || '#FF0000'}`,
          borderRadius: `${4 * stageScale}px`,
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundColor: '#000',
          pointerEvents: 'none', // 마우스 이벤트를 HTMLShapeBase로 전달
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 드래그용 헤더 */}
        <div
          style={{
            width: '100%',
            height: `${headerHeight}px`,
            backgroundColor: '#ffffff',
            borderBottom: `1px solid #333`,
            pointerEvents: shouldDisableIframe ? 'none' : 'auto', // 드래그 중이면 헤더도 비활성화하여 드래그 상태 유지
            cursor: shouldDisableIframe ? 'grabbing' : 'grab',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* 작은 점 3개 표시 */}
          <div style={{ 
            display: 'flex', 
            gap: `${3 * stageScale}px`,
            alignItems: 'center',
          }}>
            <div style={{
              width: `${3 * stageScale}px`,
              height: `${3 * stageScale}px`,
              borderRadius: '50%',
              backgroundColor: '#666',
            }} />
            <div style={{
              width: `${3 * stageScale}px`,
              height: `${3 * stageScale}px`,
              borderRadius: '50%',
              backgroundColor: '#666',
            }} />
            <div style={{
              width: `${3 * stageScale}px`,
              height: `${3 * stageScale}px`,
              borderRadius: '50%',
              backgroundColor: '#666',
            }} />
          </div>
        </div>
        
        {/* 비디오 영역 */}
        <div
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            position: 'relative',
            pointerEvents: shouldDisableIframe ? 'none' : 'auto', // 드래그 중이면 전체 비디오 영역 비활성화
          }}
        >
          {shape.width && shape.height && shape.width > 0 && shape.height > 0 ? (
            <iframe
              key={shape.id} // key 추가하여 재렌더링 최소화
              src={embedUrl}
              style={{
                width: '100%',
                height: '100%',
                minWidth: '200px',
                minHeight: '112px',
                border: 'none',
                pointerEvents: shouldDisableIframe ? 'none' : 'auto', // 드래그 중이면 iframe 비활성화
                display: 'block',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video player"
            />
          ) : (
            <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>
              Loading... ({shape.width} x {shape.height})
            </div>
          )}
        </div>
      </div>
    </HTMLShapeBase>
  );
};

// React.memo로 불필요한 리렌더링 방지 (props 비교 최적화)
export default memo(HTMLYoutube, (prevProps, nextProps) => {
  // shape의 중요한 속성만 비교
  return (
    prevProps.shape.id === nextProps.shape.id &&
    prevProps.shape.x === nextProps.shape.x &&
    prevProps.shape.y === nextProps.shape.y &&
    prevProps.shape.width === nextProps.shape.width &&
    prevProps.shape.height === nextProps.shape.height &&
    prevProps.shape.youtubeVideoId === nextProps.shape.youtubeVideoId &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.showResizeHandles === nextProps.showResizeHandles &&
    prevProps.stagePos.x === nextProps.stagePos.x &&
    prevProps.stagePos.y === nextProps.stagePos.y &&
    prevProps.stageScale === nextProps.stageScale &&
    prevProps.isAnyDragging === nextProps.isAnyDragging &&
    prevProps.isDragging === nextProps.isDragging
  );
});

